import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Badge, 
  Modal, 
  Form, 
  Input, 
  Button, 
  Select, 
  DatePicker, 
  message, 
  Card, 
  List, 
  Avatar,
  Typography,
  Row,
  Col,
  Drawer,
  Grid,
  Space,
  Tag,
  Popconfirm,
  FloatButton,
  Empty,
  Divider,
  TimePicker
} from 'antd';
import { 
  PlusOutlined, 
  CalendarOutlined, 
  ClockCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  MenuOutlined,
  AlertCircleOutlined,
  UserOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import { calendarService } from '../../services/calendarService';
import dayjs from 'dayjs';
const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;
const CalendarPage = () => {
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [modalVisible, setModalVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  useEffect(() => {
    loadEvents();
  }, []);
  const loadEvents = async () => {
    try {
      const response = await calendarService.getEvents({});
      setEvents(response.data || []);
    } catch (error) {
      console.error('Error loading events:', error);
      message.error('เกิดข้อผิดพลาดในการโหลดข้อมูล');
    }
  };
  const getListData = (value) => {
    const dateStr = value.format('YYYY-MM-DD');
    return events.filter(event => 
      dayjs(event.start_date).format('YYYY-MM-DD') === dateStr
    );
  };
  const dateCellRender = (value) => {
    const listData = getListData(value);
    return (
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {listData.slice(0, isMobile ? 2 : 3).map((item) => (
          <li key={item.id} style={{ marginBottom: 2 }}>
            <Badge
              color={getPriorityColor(item.priority)}
              text={
                <span style={{ 
                  fontSize: isMobile ? '10px' : '11px',
                  lineHeight: 1.2
                }}>
                  {item.title?.length > (isMobile ? 8 : 12) 
                    ? `${item.title.substring(0, isMobile ? 8 : 12)}...` 
                    : item.title}
                </span>
              }
            />
          </li>
        ))}
        {listData.length > (isMobile ? 2 : 3) && (
          <li>
            <Text style={{ fontSize: '9px', color: '#999' }}>
              +{listData.length - (isMobile ? 2 : 3)} เพิ่มเติม
            </Text>
          </li>
        )}
      </ul>
    );
  };
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#ff4d4f';
      case 'medium': return '#faad14';
      case 'low': return '#52c41a';
      default: return '#d9d9d9';
    }
  };
  const getPriorityText = (priority) => {
    switch (priority) {
      case 'high': return 'สูง';
      case 'medium': return 'ปานกลาง';
      case 'low': return 'ต่ำ';
      default: return 'ปกติ';
    }
  };
  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return '🔴';
      case 'medium': return '🟡';
      case 'low': return '🟢';
      default: return '⚪';
    }
  };
  const formatDateTime = (date, time) => {
    if (!date) return '';
    const dateObj = dayjs(date);
    if (time) {
      return dateObj.format('DD/MM/YYYY') + ' เวลา ' + time;
    }
    return dateObj.format('DD/MM/YYYY');
  };
  const handleAddEvent = () => {
    setEditingEvent(null);
    form.resetFields();
    form.setFieldsValue({ 
      start_date: selectedDate,
      priority: 'medium'
    });
    setModalVisible(true);
  };
  const handleEditEvent = (event) => {
    setEditingEvent(event);
    form.setFieldsValue({
      ...event,
      start_date: dayjs(event.start_date),
      end_date: event.end_date ? dayjs(event.end_date) : undefined,
      start_time: event.start_time ? dayjs(event.start_time, 'HH:mm') : undefined,
      end_time: event.end_time ? dayjs(event.end_time, 'HH:mm') : undefined
    });
    setModalVisible(true);
    if (isMobile) {
      setDrawerVisible(false);
    }
  };
  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const eventData = {
        ...values,
        start_date: values.start_date.format('YYYY-MM-DD'),
        end_date: values.end_date ? values.end_date.format('YYYY-MM-DD') : null,
        start_time: values.start_time ? values.start_time.format('HH:mm') : null,
        end_time: values.end_time ? values.end_time.format('HH:mm') : null,
        is_all_day: !values.start_time && !values.end_time
      };
      if (editingEvent) {
        await calendarService.updateEvent(editingEvent.id, eventData);
        message.success('แก้ไขกิจกรรมสำเร็จ');
      } else {
        await calendarService.createEvent(eventData);
        message.success('เพิ่มกิจกรรมสำเร็จ');
      }
      setModalVisible(false);
      form.resetFields();
      loadEvents();
    } catch (error) {
      console.error('Error saving event:', error);
      message.error('เกิดข้อผิดพลาดในการบันทึกกิจกรรม');
    } finally {
      setLoading(false);
    }
  };
  const handleDeleteEvent = async (id) => {
    try {
      await calendarService.deleteEvent(id);
      message.success('ลบกิจกรรมสำเร็จ');
      loadEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      message.error('เกิดข้อผิดพลาดในการลบกิจกรรม');
    }
  };
  const onSelect = (value) => {
    setSelectedDate(value);
    if (isMobile) {
      setDrawerVisible(true);
    }
  };
  const onPanelChange = (value) => {
    setSelectedDate(value);
  };
  const selectedDateEvents = getListData(selectedDate);
  // Render Event List Component
  const EventList = ({ showTitle = true }) => (
    <div>
      {showTitle && (
        <div style={{ 
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Title level={4} style={{ margin: 0 }}>
            📅 {selectedDate.format('DD/MM/YYYY')}
          </Title>
          {isMobile && (
            <Button 
              type="primary" 
              size="small"
              icon={<PlusOutlined />}
              onClick={handleAddEvent}
            >
              เพิ่ม
            </Button>
          )}
        </div>
      )}
      
      {selectedDateEvents.length > 0 ? (
        <List
          size="small"
          dataSource={selectedDateEvents}
          renderItem={(item) => (
            <List.Item
              style={{
                padding: isMobile ? '8px 0' : '12px 0',
                borderBottom: '1px solid #f0f0f0'
              }}
              actions={[
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleEditEvent(item)}
                  style={{ color: '#1890ff' }}
                >
                  {!isMobile && 'แก้ไข'}
                </Button>,
                <Popconfirm
                  title="คุณต้องการลบกิจกรรมนี้?"
                  onConfirm={() => handleDeleteEvent(item.id)}
                  okText="ลบ"
                  cancelText="ยกเลิก"
                >
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    danger
                  >
                    {!isMobile && 'ลบ'}
                  </Button>
                </Popconfirm>
              ]}
            >
              <List.Item.Meta
                avatar={
                  <Avatar
                    size={isMobile ? 'default' : 'large'}
                    icon={<CalendarOutlined />}
                    style={{ 
                      backgroundColor: getPriorityColor(item.priority),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  />
                }
                title={
                  <div style={{ 
                    fontSize: isMobile ? '14px' : '16px',
                    fontWeight: 600,
                    color: '#262626'
                  }}>
                    {item.title}
                  </div>
                }
                description={
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#666',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}>
                      <ClockCircleOutlined />
                      {formatDateTime(item.start_date, item.start_time)}
                    </div>
                    <div>
                      <Tag 
                        color={getPriorityColor(item.priority)}
                        style={{ fontSize: '11px' }}
                      >
                        {getPriorityIcon(item.priority)} {getPriorityText(item.priority)}
                      </Tag>
                      {item.is_all_day && (
                        <Tag color="purple" style={{ fontSize: '11px', marginLeft: 4 }}>
                          🌅 ตลอดทั้งวัน
                        </Tag>
                      )}
                    </div>
                    {item.location && (
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#666',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                      }}>
                        <EnvironmentOutlined />
                        {item.location}
                      </div>
                    )}
                    {item.description && (
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#8c8c8c',
                        marginTop: 4,
                        lineHeight: 1.4
                      }}>
                        {item.description}
                      </div>
                    )}
                    {item.created_by_name && (
                      <div style={{ 
                        fontSize: '11px', 
                        color: '#999',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                      }}>
                        <UserOutlined />
                        สร้างโดย {item.created_by_name}
                      </div>
                    )}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      ) : (
        <Empty 
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="ไม่มีกิจกรรมในวันนี้"
          style={{ 
            padding: isMobile ? '20px 0' : '40px 0',
            color: '#999'
          }}
        >
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={handleAddEvent}
            size={isMobile ? 'small' : 'default'}
          >
            เพิ่มกิจกรรมใหม่
          </Button>
        </Empty>
      )}
    </div>
  );
  return (
    <div style={{ 
      padding: isMobile ? 16 : 24,
      background: '#f5f5f5',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title 
            level={2} 
            style={{ 
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: isMobile ? '20px' : '24px'
            }}
          >
            📅 ปฏิทินกิจกรรม
          </Title>
        </Col>
        <Col>
          <Space>
            {!isMobile && (
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={handleAddEvent}
                size="large"
                style={{ borderRadius: '8px' }}
              >
                เพิ่มกิจกรรม
              </Button>
            )}
            {isMobile && selectedDateEvents.length > 0 && (
              <Button
                icon={<MenuOutlined />}
                onClick={() => setDrawerVisible(true)}
                style={{ borderRadius: '8px' }}
              >
                รายการ ({selectedDateEvents.length})
              </Button>
            )}
          </Space>
        </Col>
      </Row>
      {/* Content */}
      <Row gutter={[16, 16]}>
        {/* Calendar Section */}
        <Col xs={24} lg={isMobile ? 24 : 16}>
          <Card
            style={{ 
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: 'none'
            }}
            bodyStyle={{ padding: isMobile ? 16 : 24 }}
          >
            <Calendar
              onSelect={onSelect}
              onPanelChange={onPanelChange}
              dateCellRender={dateCellRender}
              style={{ 
                border: 'none',
                borderRadius: '8px'
              }}
              headerRender={({ value, type, onChange, onTypeChange }) => (
                <div style={{ 
                  padding: '0 8px 16px 8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 8
                }}>
                  <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                    {value.format('MMMM YYYY')}
                  </Title>
                  <Space>
                    <Button
                      size="small"
                      onClick={() => onChange(value.subtract(1, type))}
                      style={{ borderRadius: '6px' }}
                    >
                      ก่อนหน้า
                    </Button>
                    <Button
                      size="small"
                      onClick={() => onChange(dayjs())}
                      type="primary"
                      ghost
                      style={{ borderRadius: '6px' }}
                    >
                      วันนี้
                    </Button>
                    <Button
                      size="small"
                      onClick={() => onChange(value.add(1, type))}
                      style={{ borderRadius: '6px' }}
                    >
                      ถัดไป
                    </Button>
                  </Space>
                </div>
              )}
            />
          </Card>
        </Col>
        {/* Event List Section - Desktop Only */}
        {!isMobile && (
          <Col xs={24} lg={8}>
            <Card
              title={
                <div style={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span>📋 กิจกรรมวันที่ {selectedDate.format('DD/MM')}</span>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {selectedDateEvents.length} รายการ
                  </Text>
                </div>
              }
              style={{ 
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: 'none',
                height: 'fit-content'
              }}
              bodyStyle={{ padding: '16px' }}
            >
              <EventList showTitle={false} />
            </Card>
          </Col>
        )}
      </Row>
      {/* Mobile Drawer */}
      <Drawer
        title={
          <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>📋 กิจกรรม</span>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {selectedDateEvents.length} รายการ
            </Text>
          </div>
        }
        placement="bottom"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        height="70vh"
        style={{ borderRadius: '16px 16px 0 0' }}
      >
        <EventList />
      </Drawer>
      {/* Floating Action Button - Mobile Only */}
      {isMobile && (
        <FloatButton
          icon={<PlusOutlined />}
          type="primary"
          onClick={handleAddEvent}
          style={{ 
            right: 16,
            bottom: 16,
            width: 56,
            height: 56
          }}
        />
      )}
      {/* Modal Form */}
      <Modal
        title={
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: isMobile ? '16px' : '18px'
          }}>
            {editingEvent ? '✏️ แก้ไขกิจกรรม' : '➕ เพิ่มกิจกรรมใหม่'}
          </div>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={isMobile ? '90vw' : 600}
        style={isMobile ? { top: 20 } : {}}
        bodyStyle={{ padding: isMobile ? 16 : 24 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="title"
            label="📝 หัวข้อกิจกรรม"
            rules={[{ required: true, message: 'กรุณากรอกหัวข้อ' }]}
          >
            <Input 
              placeholder="หัวข้อกิจกรรม"
              size={isMobile ? 'large' : 'default'}
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="start_date"
                label="📅 วันที่เริ่ม"
                rules={[{ required: true, message: 'กรุณาเลือกวันที่' }]}
              >
                <DatePicker 
                  style={{ width: '100%', borderRadius: '8px' }} 
                  format="DD/MM/YYYY"
                  size={isMobile ? 'large' : 'default'}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="end_date" label="📅 วันที่สิ้นสุด">
                <DatePicker 
                  style={{ width: '100%', borderRadius: '8px' }} 
                  format="DD/MM/YYYY"
                  size={isMobile ? 'large' : 'default'}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="start_time" label="⏰ เวลาเริ่ม">
                <TimePicker 
                  style={{ width: '100%', borderRadius: '8px' }} 
                  format="HH:mm"
                  size={isMobile ? 'large' : 'default'}
                  placeholder="เลือกเวลา"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="end_time" label="⏰ เวลาสิ้นสุด">
                <TimePicker 
                  style={{ width: '100%', borderRadius: '8px' }} 
                  format="HH:mm"
                  size={isMobile ? 'large' : 'default'}
                  placeholder="เลือกเวลา"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="priority"
                label="🏆 ระดับความสำคัญ"
                initialValue="medium"
              >
                <Select 
                  size={isMobile ? 'large' : 'default'}
                  style={{ borderRadius: '8px' }}
                >
                  <Option value="low">🟢 ต่ำ</Option>
                  <Option value="medium">🟡 ปานกลาง</Option>
                  <Option value="high">🔴 สูง</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="location" label="📍 สถานที่">
                <Input 
                  placeholder="สถานที่จัดกิจกรรม"
                  size={isMobile ? 'large' : 'default'}
                  style={{ borderRadius: '8px' }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="📄 รายละเอียด">
            <TextArea 
              rows={4} 
              placeholder="รายละเอียดเพิ่มเติม"
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>
          <Divider />
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button 
                onClick={() => setModalVisible(false)}
                size={isMobile ? 'large' : 'default'}
                style={{ borderRadius: '8px' }}
              >
                ยกเลิก
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                size={isMobile ? 'large' : 'default'}
                style={{ borderRadius: '8px' }}
              >
                {editingEvent ? '💾 บันทึกการแก้ไข' : '➕ เพิ่มกิจกรรม'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
export default CalendarPage;